import { lockCompositionMediaPolicies, lockCompositionVersions } from './compositionMediaPolicy.js';
/**
 * VersionsRepo — typed repository over {@link DocStorePort} for
 * `creations/{id}/versions/{n}`. Version numbers are monotonic 1-based
 * integers; {@link append} allocates the next `n` from the parent creation's
 * `latestVersion` and writes both docs in one batch. Status transitions are
 * validated against the build lifecycle so a version can't jump illegally.
 */
import {
  PlatformError,
  type DocStorePort,
  type DocTxRunner,
  type ServerTimestampSentinel,
  type Uid,
} from '@djgg/core';
import { GROUPS, creationDoc, versionDoc, versionsCollection, buildJobDoc, assetGenJobDoc, assetVariantDoc, buildPipelinesDoc } from '../paths.js';
import { workshopDoc, workshopSlot, emptyWorkshop, type WorkshopState } from '../workshop.js';
import { queuedBuildJobWrite, type BuildClaimAuthority } from './buildJobs.js';
import { AssetRejectionsRepo } from './assetRejections.js';
import { WorkshopRepo } from './workshop.js';
import { SfxBindingsRepo } from './sfxBindings.js';
import { isCompositionOriginHead } from './compositionRoute.js';
import { lockCompositionPublication, assertCompositionPublicationProof, type CompositionPublicationInput } from './compositionPublication.js';
import type { GameSettings } from '../spec/types.js';
import type {
  GameDesign,
} from '../design/types.js';
import type { BuildProgress } from '../buildProgress.js';
import type { SplashReelOutcomeV1 } from '../splashReel.js';
import type {
  Creation,
  AssetVariantDoc,
  BuildStatus,
  BuildJob,
  AssetGenJob,
  CreationAsset,
  CreationVersion,
  ManifestSummary,
  TokenUsage,
  VersionAssetGen,
  VersionAssetRef,
  VersionGates,
  VisualMigration,
} from '../types.js';
import { validateRuntimeContractRefV1, type RuntimeContractRefV1 } from '../runtimeRef.js';
import type { AcceptedBuildExecutionPolicyV1, BuildAcceptancePolicyMode } from '../buildExecutionPolicy.js';
import type { GameQualityExecutionKind } from '../gameQualityContract.js';
import type { AcceptedQualityRunV1, GameQualityStage } from '../gameQualityContract.js';
import { resolveBuildExecutionPolicy } from '../buildExecutionPolicy.js';
import { buildPipelineRef, withBuildPipelineSeeds, type BuildPipelineCatalog } from '../buildPipelines.js';
import {
  canonicalJson,
  validateGameplayCapabilityLock,
  verifyGameplayCapabilityLock,
  type GameplayCapabilityLockV1,
} from '@djgg/gameplay-kit';
import { writeQualityAdmissionV2, type QualityAdmissionWriteV2 } from '../qualityAdmission.js';

/** Fields the caller supplies when appending a new version. */
export interface AppendVersionInput {
  compositionConversion?: CreationVersion['compositionConversion'];
  pipelineSnapshot?: CreationVersion['pipelineSnapshot'];
  qualityRun?: CreationVersion['qualityRun'];
  qualityRunId?: CreationVersion['qualityRunId'];
  qualitySummary?: CreationVersion['qualitySummary'];
  qualityAdmissionV2?: QualityAdmissionWriteV2;
  executionPolicy?: AcceptedBuildExecutionPolicyV1;
  executionPolicyMode?: BuildAcceptancePolicyMode;
  /** Server caller classification for the general append seam. */
  executionKind?: GameQualityExecutionKind;
  creationId: string;
  prompt: string;
  provider: string;
  model: string;
  diffSummary?: string;
  srcPath?: string;
  /** Attribution: the uid/handle this build is credited to (the requesting
   *  member, or the collaborator a superadmin attributed it to). Empty when
   *  omitted (legacy callers/tests). */
  updatedBy?: string;
  updatedByHandle?: string;
  /** The COMPLETE settings this build runs against (stamped by requestBuild;
   *  immutable once written). When present, `settingsSchemaVersion` /
   *  `settingsRev` / `settingsDiff` are stamped alongside it. */
  settingsSnapshot?: GameSettings;
  settingsSchemaVersion?: number;
  settingsRev?: number;
  settingsDiff?: string;
  /** The design doc this build runs against (stamped by requestBuild from the
   *  ACTIVE version's snapshot ?? the creation head — the design lineage
   *  follows the source lineage). `designRev` is stamped alongside it. */
  designSnapshot?: GameDesign;
  designRev?: number;
  acceptedLineage?: NonNullable<VersionGates['acceptedLineage']>;
  acceptedRecovery?: NonNullable<VersionGates['acceptedRecovery']>;
  /** The creation's attached-asset inventory at enqueue time (see
   *  {@link CreationVersion.assetsSnapshot}) — pass [] for "nothing attached";
   *  omit only for legacy callers/tests that predate the field. */
  assetsSnapshot?: VersionAssetRef[];
  /** ART-COMPLETION builds only (see {@link CreationVersion.artCompletionOf}):
   *  the seed version (the trigger-time active — the origin or a completion
   *  descendant of it) + the late-generated assets this copy build binds. */
  artCompletionOf?: number;
  artCompletionAssets?: CreationVersion['artCompletionAssets'];
  buildMode?: CreationVersion['buildMode'];
  presentationEdit?: CreationVersion['presentationEdit'];
  visualMigration?: VisualMigration;
  creationRun?: CreationVersion['creationRun'];
  /** Explicit, trusted lock copied from the selected source version. It is
   * never inferred from source paths or creation pointers. */
  capabilityLock?: GameplayCapabilityLockV1;
}

/** The ONE mapping of an attached-asset list onto version-doc snapshot refs
 *  (requestBuild + createFork both stamp through here). */
export function toVersionAssetRefs(
  assets: readonly CreationAsset[] | undefined,
): VersionAssetRef[] {
  return (assets ?? []).map((a) => ({ name: a.name, variantId: a.variantId, kind: a.kind,
    ...(a.contentSha256 ? { contentSha256: a.contentSha256 } : {}) }));
}

/**
 * Legal build-status transitions. Forward through the pipeline; `failed` is
 * reachable from any non-terminal state; terminal states (`live`, `failed`) go
 * nowhere. `queued` is the initial state.
 */
export const BUILD_STATUS_TRANSITIONS: Readonly<Record<BuildStatus, readonly BuildStatus[]>> = {
  queued: ['generating', 'failed'],
  generating: ['gating', 'failed'],
  gating: ['publishing', 'failed'],
  publishing: ['live', 'failed'],
  live: [],
  failed: [],
};

/** True when `from → to` is a legal build-status transition. */
export function isValidBuildTransition(from: BuildStatus, to: BuildStatus): boolean {
  return BUILD_STATUS_TRANSITIONS[from].includes(to);
}

/** Historical Postgres rows may still contain the neutral timestamp sentinel
 * inside buildTiming JSON. Never carry that object into a new JSON write (the
 * adapter would resolve it to the current write time) or use it in elapsed-time
 * arithmetic. The version's immutable createdAt is the accepted-at authority
 * used by the bounded repair migration as well. */
function acceptedAtOrCreatedAt(version: Pick<CreationVersion, 'buildTiming' | 'createdAt'>): number {
  const acceptedAt: unknown = version.buildTiming?.acceptedAt;
  return typeof acceptedAt === 'number' && Number.isFinite(acceptedAt) && acceptedAt >= 0
    ? acceptedAt
    : version.createdAt;
}

const EMPTY_GATES: VersionGates = { typecheck: false, tests: false, build: false, notes: '' };
const ZERO_TOKENS: TokenUsage = { input: 0, output: 0 };

function sameJson(left: unknown, right: unknown): boolean {
  if (left === undefined || right === undefined) return left === right;
  try { return canonicalJson(left) === canonicalJson(right); }
  catch { return false; }
}

/** Fields accepted transactionally with the command and therefore immutable
 * for the lifetime of a quality run. Later stages may only add the finalized
 * specification and core evidence. */
function sameQualityRunAuthority(left: AcceptedQualityRunV1, right: AcceptedQualityRunV1): boolean {
  return sameJson({
    schemaVersion: left.schemaVersion,
    recipe: left.recipe,
    executionKind: left.executionKind,
    originalInputs: left.originalInputs,
    seed: left.seed ?? null,
    roles: left.roles,
    budget: left.budget,
  }, {
    schemaVersion: right.schemaVersion,
    recipe: right.recipe,
    executionKind: right.executionKind,
    originalInputs: right.originalInputs,
    seed: right.seed ?? null,
    roles: right.roles,
    budget: right.budget,
  });
}

/** A terminal same-state call is idempotent only when it asks for no data
 * change. This prevents a stale publisher from using `live -> live` or
 * `failed -> failed` as an artifact/path/digest overwrite channel. */
function terminalPatchIsIdentical(
  current: CreationVersion,
  patch: NonNullable<Parameters<VersionsRepo['setStatus']>[3]>,
): boolean {
  for (const [key, value] of Object.entries(patch)) {
    const expected = key === 'gates'
      ? { ...current.gates, ...(value as Partial<VersionGates>) }
      : value;
    const actual = key === 'gates'
      ? current.gates
      : current[key as keyof CreationVersion];
    if (!sameJson(actual, expected)) return false;
  }
  return true;
}

/**
 * The `versions/{n}` doc for a fresh QUEUED build — the ONE shape, shared by
 * {@link VersionsRepo.append} (iterate: n allocated from `latestVersion`) and
 * `CreationsRepo.createFork` (the fork's seeded v1, written inside the fork's
 * birth batch where append can't run — the creation doesn't exist yet).
 */
export function queuedVersionWrite(
  input: Omit<AppendVersionInput, 'creationId'>,
  n: number,
  ts: ServerTimestampSentinel,
): Record<string, unknown> {
  const write: Record<string, unknown> = {
    n,
    prompt: input.prompt,
    provider: input.provider,
    model: input.model,
    diffSummary: input.diffSummary ?? '',
    buildStatus: 'queued',
    gates: {
      ...EMPTY_GATES,
      ...(input.acceptedLineage ? { acceptedLineage: { ...input.acceptedLineage } } : {}),
      ...(input.acceptedRecovery ? { acceptedRecovery: structuredClone(input.acceptedRecovery) } : {}),
    },
    srcPath: input.srcPath ?? '',
    bundlePath: '',
    renderManifestPath: '',
    tokenUsage: { ...ZERO_TOKENS },
    costUsd: 0,
    updatedBy: input.updatedBy ?? '',
    updatedByHandle: input.updatedByHandle ?? '',
    createdAt: ts,
    finishedAt: null,
    buildTiming: {
      acceptedAt: ts,
      workerStartedAt: null,
      readyAt: null,
      queueIntervals: [],
      queueMs: 0,
      buildDurationMs: null,
      totalElapsedMs: null,
    },
  };
  if (input.pipelineSnapshot) write.pipelineSnapshot = structuredClone(input.pipelineSnapshot);
  if (input.qualityRun) write.qualityRun = structuredClone(input.qualityRun);
  if (input.qualityRunId !== undefined) write.qualityRunId = input.qualityRunId;
  if (input.qualitySummary !== undefined) write.qualitySummary = structuredClone(input.qualitySummary);
  if (input.executionPolicy) write.executionPolicy = structuredClone(input.executionPolicy);
  if (input.compositionConversion) write.compositionConversion = structuredClone(input.compositionConversion);
  if (input.settingsSnapshot) {
    write.settingsSnapshot = { ...input.settingsSnapshot };
    write.settingsSchemaVersion = input.settingsSchemaVersion ?? 0;
    write.settingsRev = input.settingsRev ?? 0;
    write.settingsDiff = input.settingsDiff ?? '';
  }
  if (input.designSnapshot) {
    write.designSnapshot = { ...input.designSnapshot };
    write.designRev = input.designRev ?? 0;
    write.designAmendment = '';
  }
  if (input.assetsSnapshot) {
    write.assetsSnapshot = input.assetsSnapshot.map((a) => ({ ...a }));
  }
  if (input.artCompletionOf !== undefined) {
    write.artCompletionOf = input.artCompletionOf;
    write.artCompletionAssets = (input.artCompletionAssets ?? []).map((a) => ({ ...a }));
  }
  if (input.buildMode) write.buildMode = input.buildMode;
  if (input.presentationEdit) write.presentationEdit = structuredClone(input.presentationEdit);
  if (input.visualMigration) write.visualMigration = input.visualMigration;
  if (input.creationRun) write.creationRun = structuredClone(input.creationRun);
  if (input.capabilityLock) write.capabilityLock = validateGameplayCapabilityLock(input.capabilityLock);
  return write;
}

export class VersionsRepo {
  constructor(
    private readonly store: DocStorePort & Partial<DocTxRunner>,
    private readonly now: () => number = () => Date.now(),
  ) {}

  /** Allocate the artwork version, queue entry and busy state under the
   * creation lock. Duplicate callbacks cannot overwrite a queued version. */
  async appendArtCompletion(input: AppendVersionInput, expectedActive: number, jobId: string, originVersion: number): Promise<CreationVersion | null> {
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'art completion requires transactions');
    if (input.artCompletionOf !== expectedActive || !input.artCompletionAssets?.length) {
      throw new PlatformError('validation', 'art completion requires assets and a matching seed');
    }
    const capabilityLock = input.capabilityLock ? await verifyGameplayCapabilityLock(input.capabilityLock) : undefined;
    const n = await this.store.runTransaction(async tx => {
      const parent = await tx.get<Creation>(creationDoc(input.creationId));
      if (!parent || parent.activeVersion !== expectedActive || parent.buildState !== 'idle') return null;
      if (await tx.get(buildJobDoc(jobId))) return null;
      const seed = await tx.get<CreationVersion>(versionDoc(input.creationId, expectedActive));
      if (seed?.buildStatus !== 'live' || seed.srcPath !== input.srcPath) return null;
      if (seed.executionPolicy?.composition) {
        throw new PlatformError('validation', 'Legacy art completion cannot extend composed authority');
      }
      const originSeed = originVersion === expectedActive
        ? seed
        : await tx.get<CreationVersion>(versionDoc(input.creationId, originVersion));
      const pipelineCatalog = withBuildPipelineSeeds(await tx.get<BuildPipelineCatalog>(buildPipelinesDoc()));
      const acceptanceMode = input.executionPolicyMode ?? 'legacy';
      if ((acceptanceMode === 'verified-core-v2') !== !!input.qualityAdmissionV2) {
        throw new PlatformError('validation', 'verified-core-v2 completion mode and admission authority must be supplied together');
      }
      const executionPolicy = resolveBuildExecutionPolicy({
        catalog: pipelineCatalog,
        mode: acceptanceMode,
        executionKind: 'automatic-art-completion',
        requested: input.pipelineSnapshot ? buildPipelineRef(input.pipelineSnapshot) : seed.pipelineSnapshot ? buildPipelineRef(seed.pipelineSnapshot) : undefined,
        active: seed.pipelineSnapshot,
        qualityRun: seed.qualityRun ?? originSeed?.qualityRun,
        qualityAdmission: input.qualityAdmissionV2?.admission,
      });
      const workshop = (await tx.get<{ state: WorkshopState }>(workshopDoc(input.creationId)))?.state ?? emptyWorkshop();
      const selections = workshop.selections.filter(s => s.originVersion === originVersion);
      const assets = [...input.artCompletionAssets!];
      for (const selection of selections) {
        const variant = await tx.get<AssetVariantDoc>(assetVariantDoc(input.creationId, selection.variantId));
        if (!variant || variant.kind === 'cover' || variant.reviewState === 'rejected' || variant.reviewState === 'superseded') continue;
        await new AssetRejectionsRepo(this.store).assertAllowed(input.creationId, variant.contentSha256, parent.assetPolicyRevision ?? 0, tx);
        const previous = assets.findIndex(a => workshopSlot(a) === selection.slot);
        if (previous >= 0) assets.splice(previous, 1);
        const entityName = variant.strip?.entityName ?? input.artCompletionAssets?.find(a => workshopSlot(a) === selection.slot)?.entityName;
        assets.push({ variantId: variant.variantId, kind: variant.kind, name: variant.name,
          url: variant.url, prompt: variant.prompt, ...(variant.contentSha256 ? { contentSha256: variant.contentSha256 } : {}),
          ...(variant.orientation ? { orientation: variant.orientation } : {}),
          ...(variant.strip ? { strip: variant.strip } : {}), ...(entityName ? { entityName } : {}) });
      }
      // Base replacement cannot reuse strips from the previous character.
      const replacedNames = new Set(assets.filter(a => a.kind === 'sprite').map(a => a.name));
      const bases = new Set([...assets.filter(a => a.kind === 'sprite').map(a => a.variantId),
        ...(parent.assets ?? []).filter(a => a.kind === 'sprite' && !replacedNames.has(a.name)).map(a => a.variantId)]);
      const applied = new Set((seed.assetsSnapshot ?? []).map(a => a.variantId));
      const compatible = assets.filter(a => (!a.strip || bases.has(a.strip.referenceVariantId)) && !applied.has(a.variantId));
      for (const asset of compatible) await new AssetRejectionsRepo(this.store).assertAllowed(input.creationId, asset.contentSha256, parent.assetPolicyRevision ?? 0, tx);
      if (!compatible.length) return null;
      const next = parent.latestVersion + 1;
      const ts = this.store.serverTimestamp();
      await tx.set(buildJobDoc(jobId), queuedBuildJobWrite({ jobId, creationId: input.creationId, version: next,
        ...(executionPolicy.acceptanceMode === 'verified-core'
          ? { requiredWorkerCapability: 'verified-core-v1' as const }
          : executionPolicy.acceptanceMode === 'verified-core-v2'
            ? { requiredWorkerCapability: 'verified-core-v2' as const }
            : {}) }, ts));
      const qualityAuthority = input.qualityAdmissionV2
        ? await writeQualityAdmissionV2(tx, input.qualityAdmissionV2, {
            jobId,
            creationId: input.creationId,
            version: next,
            commandId: jobId,
            executionKind: 'automatic-art-completion',
            baseVersion: expectedActive,
          }, this.now())
        : undefined;
      for (const selection of selections) if (compatible.some(a => a.variantId === selection.variantId)) selection.snapshotVersion = next;
      if (selections.length) await tx.set(workshopDoc(input.creationId), { state: workshop });
      const { executionPolicy: _callerExecutionPolicy, executionPolicyMode: _executionPolicyMode,
        executionKind: _executionKind, qualityRun: _callerQualityRun,
        qualityAdmissionV2: _qualityAdmissionV2, ...acceptedInput } = input;
      await tx.set(versionDoc(input.creationId, next), queuedVersionWrite({ ...acceptedInput,
        pipelineSnapshot: executionPolicy.pipelineSnapshot, executionPolicy,
        artCompletionAssets: compatible, ...(capabilityLock ? { capabilityLock } : {}),
        ...(qualityAuthority ? {
          qualityRunId: qualityAuthority.runId,
          qualitySummary: { recipe: 'verified-core@2' as const, phase: 'contract-pending', proofDebtCodes: [] },
        } : {}),
      }, next, ts));
      await tx.update(creationDoc(input.creationId), { latestVersion: next, buildState: 'building', updatedAt: ts });
      return next;
    });
    return n === null ? null : this.get(input.creationId, n);
  }

  /**
   * Append the next version to a creation. Reads the parent's `latestVersion`,
   * writes `versions/{n+1}` (status `queued`) and bumps the parent's
   * `latestVersion` + `updatedAt` in one transaction. Throws
   * `PlatformError('not-found')` if the creation is absent.
   */
  async append(input: AppendVersionInput): Promise<CreationVersion> {
    const executionKind = input.executionKind ?? 'source-preserving-copy';
    const acceptanceMode = input.executionPolicyMode ?? 'legacy';
    if ((acceptanceMode === 'verified-core-v2') !== !!input.qualityAdmissionV2) {
      throw new PlatformError('validation', 'verified-core-v2 append mode and admission authority must be supplied together');
    }
    if (input.qualityAdmissionV2) {
      throw new PlatformError('validation', 'generic append cannot create complete verified-core-v2 job authority');
    }
    // Structural validation alone is not authentication: every direct append
    // path (including art-completion and bounded ops copy jobs) recomputes the
    // digest before the immutable queued row is written.
    const capabilityLock = input.capabilityLock
      ? await verifyGameplayCapabilityLock(input.capabilityLock)
      : undefined;
    const { executionPolicy: _callerExecutionPolicy, executionPolicyMode: _executionPolicyMode,
      executionKind: _executionKind, qualityAdmissionV2: _qualityAdmissionV2, ...acceptedInput } = input;
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'version append requires transactions');
    const n = await this.store.runTransaction(async tx => {
      const parent = await tx.get<{ latestVersion?: number; activeVersion?: number }>(creationDoc(input.creationId));
      if (!parent) throw new PlatformError('not-found', `append: creation ${input.creationId} not found`);
      for (const sourceVersion of new Set([parent.latestVersion, parent.activeVersion])) {
        if (sourceVersion && (await tx.get<CreationVersion>(versionDoc(input.creationId, sourceVersion)))?.executionPolicy?.composition) {
          throw new PlatformError('validation', 'Generic append cannot replace composed authority');
        }
      }
      const next = (parent.latestVersion ?? 0) + 1;
      if (await tx.get<CreationVersion>(versionDoc(input.creationId, next))) {
        throw new PlatformError('conflict', `version ${input.creationId}/${next} already exists`);
      }
      const pipelineCatalog = withBuildPipelineSeeds(await tx.get<BuildPipelineCatalog>(buildPipelinesDoc()));
      const executionPolicy = resolveBuildExecutionPolicy({
        catalog: pipelineCatalog,
        mode: acceptanceMode,
        executionKind,
        requested: input.pipelineSnapshot ? buildPipelineRef(input.pipelineSnapshot) : undefined,
        active: input.pipelineSnapshot,
        qualityRun: input.qualityRun,
        qualityAdmission: undefined,
      });
      const ts = this.store.serverTimestamp();
      await tx.set(versionDoc(input.creationId, next), queuedVersionWrite({
        ...acceptedInput,
        pipelineSnapshot: executionPolicy.pipelineSnapshot,
        executionPolicy,
        ...(capabilityLock ? { capabilityLock } : {}),
      }, next, ts));
      await tx.update(creationDoc(input.creationId), { latestVersion: next, updatedAt: ts });
      return next;
    });
    return (await this.get(input.creationId, n))!;
  }

  /** Fetch one version, or null. */
  async get(creationId: string, n: number): Promise<CreationVersion | null> {
    return this.store.getDoc<CreationVersion>(versionDoc(creationId, n));
  }

  /** Claim-compatible quality-state CAS. The immutable input identity cannot
   * change, and a re-drive may repeat only the exact state it already wrote. */
  async transitionQualityRun(
    creationId: string,
    n: number,
    expectedStages: readonly GameQualityStage[],
    next: AcceptedQualityRunV1,
  ): Promise<void> {
    const won = await this.store.guardedUpdate<CreationVersion>(
      versionDoc(creationId, n),
      current => current !== null
        && current.buildStatus !== 'live'
        && current.buildStatus !== 'failed'
        && !!current.qualityRun
        && sameQualityRunAuthority(current.qualityRun, next)
        && (!current.qualityRun.spec || sameJson(current.qualityRun.spec, next.spec))
        && (!current.qualityRun.core || sameJson(current.qualityRun.core, next.core))
        && (expectedStages.includes(current.qualityRun.stage)
          || sameJson(current.qualityRun, next)),
      { qualityRun: structuredClone(next) },
    );
    if (!won) throw new PlatformError('conflict', 'quality run changed or the version is terminal');
  }

  /** Commit the verified core and release its accepted media children as one
   * claim-owned transaction. A stolen worker cannot open a media wave, and a
   * crash can leave neither half of the barrier visible on its own. */
  async commitVerifiedCoreAndReleaseMedia(input: {
    creationId: string;
    n: number;
    jobId: string;
    workerId: string;
    next: AcceptedQualityRunV1;
    mediaJobIds: readonly string[];
  }): Promise<void> {
    if (!this.store.runTransaction) {
      throw new PlatformError('unavailable', 'verified-core commit requires server transactions');
    }
    const ids = [...new Set(input.mediaJobIds)].sort();
    const core = input.next.core;
    if (ids.length !== input.mediaJobIds.length || !core || input.next.stage !== 'core-verified') {
      throw new PlatformError('validation', 'verified-core commit requires unique media jobs and exact core evidence');
    }
    await this.store.runTransaction(async tx => {
      const job = await tx.get<BuildJob>(buildJobDoc(input.jobId));
      if (!job || job.creationId !== input.creationId || job.version !== input.n
        || job.status !== 'claimed' || job.workerId !== input.workerId) {
        throw new PlatformError('conflict', 'verified-core commit is not owned by the active build claim');
      }
      const current = await tx.get<CreationVersion>(versionDoc(input.creationId, input.n));
      if (!current || current.buildStatus === 'live' || current.buildStatus === 'failed' || !current.qualityRun
        || !sameQualityRunAuthority(current.qualityRun, input.next)
        || (current.qualityRun.spec && !sameJson(current.qualityRun.spec, input.next.spec))
        || (current.qualityRun.core && !sameJson(current.qualityRun.core, input.next.core))
        || (!['spec-ready', 'core-verified'].includes(current.qualityRun.stage)
          && !sameJson(current.qualityRun, input.next))) {
        throw new PlatformError('conflict', 'verified-core quality authority changed or version is terminal');
      }
      const rows: Array<{ id: string; job: AssetGenJob }> = [];
      for (const id of ids) {
        const mediaJob = await tx.get<AssetGenJob>(assetGenJobDoc(id));
        const prerequisite = mediaJob?.prerequisite;
        if (!mediaJob || mediaJob.creationId !== input.creationId
          || prerequisite?.kind !== 'verified-core'
          || prerequisite.runInputDigest !== input.next.originalInputs.inputDigest
          || (prerequisite.mediaBasisDigest !== null
            && prerequisite.mediaBasisDigest !== core.mediaBasisDigest)) {
          throw new PlatformError('conflict', `verified-core media job ${id} has a different origin or basis`);
        }
        rows.push({ id, job: mediaJob });
      }
      await tx.update(versionDoc(input.creationId, input.n), { qualityRun: structuredClone(input.next) });
      for (const { id, job: mediaJob } of rows) {
        if (mediaJob.status !== 'awaiting-core') continue;
        await tx.update(assetGenJobDoc(id), {
          status: 'queued',
          prerequisite: {
            kind: 'verified-core',
            runInputDigest: input.next.originalInputs.inputDigest,
            mediaBasisDigest: core.mediaBasisDigest,
            releasedAt: this.store.serverTimestamp(),
          },
        });
      }
    });
  }

  /** Capture this attempt's bounded review excerpt without changing the text
   * generation consumes. Attempts may replace the snapshot while generating;
   * terminal versions retain the evidence their published audit consumed. */
  async recordGenerationContext(
    creationId: string,
    n: number,
    input: Pick<NonNullable<VersionGates['generationContext']>, 'editMode' | 'referenceMaterial'>,
  ): Promise<NonNullable<VersionGates['generationContext']>> {
    const current = await this.get(creationId, n);
    if (!current) throw new PlatformError('not-found', `generation context: version ${creationId}/${n} not found`);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(input.referenceMaterial));
    const referenceMaterialSha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    const limit = 24000;
    const context = {
      editMode: input.editMode,
      referenceMaterialOriginalLength: input.referenceMaterial.length,
      referenceMaterialSha256,
      referenceMaterial: input.referenceMaterial.length > limit
        ? `${input.referenceMaterial.slice(0, limit)}\n[TRUNCATED: reference material; ${input.referenceMaterial.length - limit} characters omitted]`
        : input.referenceMaterial,
    };
    if (current.buildStatus !== 'queued' && current.buildStatus !== 'generating') {
      // A reclaimed composed publication reuses immutable evidence. It cannot
      // reset lifecycle state or reinterpret changed reference material.
      if (current.executionPolicy?.composition && ['gating','publishing'].includes(current.buildStatus)
        && sameJson(current.gates.generationContext, context)) return context;
      throw new PlatformError('conflict', 'generation evidence can only be recorded before gating');
    }
    const won = await this.store.guardedUpdate<CreationVersion>(
      versionDoc(creationId, n),
      (live) => live !== null && live.buildStatus === current.buildStatus && JSON.stringify(live.gates) === JSON.stringify(current.gates),
      { gates: { ...current.gates, generationContext: context } },
    );
    if (!won) throw new PlatformError('conflict', 'version evidence changed while freezing generation context');
    return context;
  }

  /** List a creation's versions, newest `n` first. */
  async list(creationId: string): Promise<CreationVersion[]> {
    return this.store.query<CreationVersion>(versionsCollection(creationId), {
      orderBy: [['n', 'desc']],
    });
  }

  /** ITERATE builds attributed to `uid` across every creation — `n > 1` (v1 is
   *  the initial build, never an iteration). The `iterations` badge metric;
   *  cross-parent, counted at enqueue time (requestBuild stamps `updatedBy`). */
  async countIterationsBy(uid: Uid): Promise<number> {
    return this.store.countGroup(GROUPS.versions, [
      ['updatedBy', '==', uid],
      ['n', '>', 1],
    ]);
  }

  /**
   * Transition a version's `buildStatus`, validating the move. Optionally
   * updates gates, token usage, and output paths in the same write. Sets
   * `finishedAt` when moving to a terminal status. Throws
   * `PlatformError('not-found')` if absent, `PlatformError('conflict')` on an
   * illegal transition or a lost compare-and-set race. Every lifecycle write
   * atomically checks the status and publication fields it read, so legacy,
   * runtime-contract, and failure writers cannot finalize competing results.
   */
  async setStatus(
    creationId: string,
    n: number,
    to: BuildStatus,
    patch: {
      gates?: Partial<VersionGates>;
      tokenUsage?: TokenUsage;
      /** Estimated USD for the build's edit rounds (see CreationVersion.costUsd). */
      costUsd?: number;
      bundlePath?: string;
      renderManifestPath?: string;
      /** Creator-facing digest of the published render manifest (stamped on
       *  `live` transitions by the builder). */
      manifestSummary?: ManifestSummary;
      srcPath?: string;
      /** gs:// path of the published headless sim bundle (Phase-2 online host). */
      simPath?: string;
      /** The creation-shell version the bundle was built against (A4). */
      shellVersion?: string;
      /** The shell's adapter-API generation at build time (A4). */
      shellApi?: number;
      /** Set when the build fell back to a non-requested engine (R3-6). */
      actualProvider?: string;
      actualModel?: string;
      /** gs:// URI of the enrichment design brief in the private logs bucket
       *  (stamped by the builder alongside the `generating` transition). */
      designBriefGcsUri?: string;
      /** gs:// URI of the build's trace.json in the private logs bucket
       *  (stamped by the worker owning the TERMINAL transition — live or
       *  failed; an aborted/claim-lost worker never stamps). */
      traceGcsUri?: string;
      /** gs:// URI of the build's debug-bundle.json (failed-gate builds only —
       *  see CreationVersion.debugBundleGcsUri). */
      debugBundleGcsUri?: string;
      /** Build-time asset-generation provenance (stamped on `live` when the
       *  build ran plan generation). */
      assetGen?: VersionAssetGen;
      /** Refreshed attachment inventory (stamped on `live` when the build
       *  itself attached generated assets — the request-time snapshot predates
       *  them, and the studio's iteration history diffs consecutive
       *  snapshots). */
      assetsSnapshot?: VersionAssetRef[];
      posterUrl?: string | null;
      /** Public URL of this version's recorded splash-reel clip, in its own
       *  immutable version directory. Version-owned and stamped once here; the
       *  creation row only ever MIRRORS the active version's value (see
       *  CreationsRepo setBuildOutcome / setAcceptedBuildOutcome /
       *  setActiveVersion), so a rollback carries the right clip with it. */
      previewUrl?: string | null;
      /** Immutable sidecar reference. It is accepted only on a publishing →
       * live transition; terminal rows can never be patched with one. */
      runtimeContract?: RuntimeContractRefV1;
      /** Final verified-core state, stamped atomically with publication. */
      qualityRun?: AcceptedQualityRunV1;
    } = {},
    options: {
      requirePublishingWithoutRuntimeContract?: boolean;
      compositionPublication?: CompositionPublicationInput;
      compositionClaim?: { jobId: string; claim: BuildClaimAuthority };
    } = {},
  ): Promise<void> {
    const current = await this.get(creationId, n);
    if (!current) {
      throw new PlatformError('not-found', `setStatus: version ${creationId}/${n} not found`);
    }
    if (current.executionPolicy?.compositionV2 || current.pipelineSnapshot?.id === 'composition-private-v2' || current.executionPolicy?.pipelineSnapshot?.id === 'composition-private-v2') {
      throw new PlatformError('conflict', 'Private composition V2 cannot use generic version lifecycle/publication');
    }
    if (current.buildStatus === to) {
      if (to === 'live' || to === 'failed') {
        if (!terminalPatchIsIdentical(current, patch)) {
          throw new PlatformError(
            'conflict',
            `terminal version ${creationId}/${n} cannot be patched through ${to} → ${to}`,
          );
        }
        return;
      }
      // Non-terminal retries retain the historical merge behavior.
    } else if (!isValidBuildTransition(current.buildStatus, to)) {
      throw new PlatformError(
        'conflict',
        `illegal build transition ${current.buildStatus} → ${to} (version ${creationId}/${n})`,
      );
    }

    const data: Record<string, unknown> = { buildStatus: to };
    const composed = !!current.executionPolicy?.composition || current.pipelineSnapshot?.config?.assembly !== undefined;
    if (composed && to === 'failed') {
      throw new PlatformError('conflict', 'Composed failure requires atomic claim-fenced termination');
    }
    if (composed && !['generating', 'gating', 'publishing', 'live'].includes(to)) {
      throw new PlatformError('conflict', 'Composition lifecycle claim only advances active build phases');
    }
    if (to === 'live' && current.executionPolicy?.composition) {
      const gates = { ...current.gates, ...patch.gates };
      const proof = gates.compositionProof;
      const authority = current.executionPolicy.composition;
      if (!proof || proof.authorityDigest !== authority.digest || proof.closureDigest !== authority.closureDigest
        || ![proof.sourceDigest, proof.distDigest, proof.nativeDigest, proof.resolutionDigest, gates.srcSha256].every(value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value))
        || !gates.typecheck || !gates.tests || !gates.build
        || !gates.completion?.gameplay.complete || !gates.completion.media.complete || !gates.completion.publication.complete
        || !(patch.srcPath ?? current.srcPath) || !(patch.simPath ?? current.simPath)
        || !(patch.bundlePath ?? current.bundlePath)?.endsWith(`/demo/${proof.distDigest}`)) {
        throw new PlatformError('validation', 'Composed publication requires exact source/runtime/native/bundle proof and complete artifacts');
      }
    }
    if (to === 'live' && composed && !options.compositionPublication) {
      throw new PlatformError('conflict', 'Composed publication requires atomic claim and activation authority');
    }
    if (options.compositionPublication && (to !== 'live' || !composed)) {
      throw new PlatformError('conflict', 'Composition publication authority cannot publish a legacy version');
    }
    if (options.compositionClaim && (to === 'live' || !composed)) {
      throw new PlatformError('conflict', 'Composition lifecycle claim cannot mutate this version');
    }
    if (composed && to !== 'live' && !options.compositionClaim) {
      throw new PlatformError('conflict', 'Composed lifecycle mutation requires its active job claim');
    }
    if (patch.gates) data.gates = { ...current.gates, ...patch.gates };
    if (patch.tokenUsage) data.tokenUsage = { ...patch.tokenUsage };
    if (patch.costUsd !== undefined) data.costUsd = patch.costUsd;
    if (patch.bundlePath !== undefined) data.bundlePath = patch.bundlePath;
    if (patch.renderManifestPath !== undefined) data.renderManifestPath = patch.renderManifestPath;
    if (patch.manifestSummary !== undefined) data.manifestSummary = patch.manifestSummary;
    if (patch.srcPath !== undefined) data.srcPath = patch.srcPath;
    if (patch.simPath !== undefined) data.simPath = patch.simPath;
    if (patch.shellVersion !== undefined) data.shellVersion = patch.shellVersion;
    if (patch.shellApi !== undefined) data.shellApi = patch.shellApi;
    if (patch.actualProvider !== undefined) data.actualProvider = patch.actualProvider;
    if (patch.actualModel !== undefined) data.actualModel = patch.actualModel;
    if (patch.designBriefGcsUri !== undefined) data.designBriefGcsUri = patch.designBriefGcsUri;
    if (patch.traceGcsUri !== undefined) data.traceGcsUri = patch.traceGcsUri;
    if (patch.debugBundleGcsUri !== undefined) data.debugBundleGcsUri = patch.debugBundleGcsUri;
    if (patch.assetGen !== undefined) data.assetGen = patch.assetGen;
    if (patch.assetsSnapshot !== undefined) data.assetsSnapshot = patch.assetsSnapshot;
    if (patch.posterUrl !== undefined) data.posterUrl = patch.posterUrl;
    if (patch.previewUrl !== undefined) data.previewUrl = patch.previewUrl;
    if (patch.qualityRun !== undefined) {
      if (to !== 'live' || current.buildStatus !== 'publishing'
        || current.qualityRun?.stage !== 'core-verified'
        || patch.qualityRun.stage !== 'published'
        || current.qualityRun.originalInputs.inputDigest !== patch.qualityRun.originalInputs.inputDigest
        || !sameJson(current.qualityRun.core, patch.qualityRun.core)) {
        throw new PlatformError('conflict', 'quality run may only publish the exact verified core with the version');
      }
      data.qualityRun = structuredClone(patch.qualityRun);
    }
    if (patch.runtimeContract !== undefined) {
      if (to !== 'live' || current.buildStatus !== 'publishing') {
        throw new PlatformError(
          'conflict',
          `runtime contract may only be stamped on publishing → live (version ${creationId}/${n})`,
        );
      }
      const runtimeContract = validateRuntimeContractRefV1(patch.runtimeContract);
      if (!current.capabilityLock) {
        throw new PlatformError('conflict', `runtime contract requires the queued capability lock on version ${creationId}/${n}`);
      }
      const queuedCapabilityLock = await verifyGameplayCapabilityLock(current.capabilityLock);
      if (current.runtimeContract !== undefined && current.runtimeContract !== null) {
        throw new PlatformError('conflict', `runtime contract is already stamped on version ${creationId}/${n}`);
      }
      if (runtimeContract.capabilityDigest !== queuedCapabilityLock.digest) {
        throw new PlatformError('conflict', `runtime contract capability digest does not match queued lock on version ${creationId}/${n}`);
      }
      const sidecarPath = /^gs:\/\/([^/]+)\/([^/]+)\/(\d+)\/runtime-contract(?:\.([0-9a-f]{64}))?\.json$/.exec(runtimeContract.path);
      if (
        !sidecarPath || sidecarPath[2] !== creationId || sidecarPath[3] !== String(n)
        || (sidecarPath[4] !== undefined && sidecarPath[4] !== runtimeContract.sha256)
      ) {
        throw new PlatformError(
          'validation',
          `runtime contract path must exactly target ${creationId}/${n} and bind its content digest`,
        );
      }
      const artifactRoot = `gs://${sidecarPath[1]}/${creationId}/${n}`;
      const sourcePath = patch.srcPath ?? current.srcPath;
      const simPath = patch.simPath ?? current.simPath;
      if (sourcePath !== `${artifactRoot}/src.tgz`) {
        throw new PlatformError('validation', `runtime contract source path must exactly target ${creationId}/${n}/src.tgz`);
      }
      const simObject = typeof simPath === 'string'
        ? new RegExp(`^${artifactRoot.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/sim(?:\\.([0-9a-f]{64}))?\\.mjs$`).exec(simPath)
        : null;
      if (!simObject) {
        throw new PlatformError('validation', `runtime contract sim path must exactly target ${creationId}/${n}`);
      }
      data.runtimeContract = runtimeContract;
    }
    if (to === 'live' || to === 'failed') {
      data.finishedAt = this.store.serverTimestamp();
      if (current.buildTiming) {
        const readyAt = this.now();
        const acceptedAt = acceptedAtOrCreatedAt(current);
        const openQueueMs = current.buildTiming.queueIntervals.reduce(
          (sum, interval) => interval.claimedAt == null
            ? sum + Math.max(0, readyAt - interval.enteredAt)
            : sum,
          0,
        );
        const totalElapsedMs = Math.max(0, readyAt - acceptedAt);
        const queueMs = current.buildTiming.workerStartedAt == null
          ? totalElapsedMs
          : current.buildTiming.queueMs + openQueueMs;
        data.buildTiming = {
          ...current.buildTiming,
          readyAt,
          queueMs,
          // A build that was cancelled or failed while still queued never had
          // worker time. Keep that duration unavailable instead of presenting
          // total waiting time as a celebrated build duration.
          buildDurationMs: current.buildTiming.workerStartedAt == null
            ? null
            : Math.max(0, totalElapsedMs - queueMs),
          totalElapsedMs,
        };
      }
    }

    if (options.requirePublishingWithoutRuntimeContract && to !== 'live') {
      throw new PlatformError('validation', 'publishing-without-runtime guard requires a live transition');
    }

    // Compare every overwritten field as well as the publication dependencies
    // inherited from the row. A status-only guard would still let a same-state
    // publishing retry replace an artifact or a sibling gate after our read,
    // then combine that newer value with this attempt's terminal result. The
    // independent progress pointer is intentionally outside this comparison.
    const comparedFields = new Set<keyof CreationVersion>([
      ...Object.keys(data) as Array<keyof CreationVersion>,
      'gates', 'srcPath', 'simPath', 'bundlePath', 'renderManifestPath',
      'capabilityLock', 'runtimeContract',
      ...(to === 'live' && composed ? ['executionPolicy', 'pipelineSnapshot', 'compositionConversion'] as const : []),
    ]);
    const accepts = (live: CreationVersion | null) => live !== null
        && [...comparedFields].every((field) => sameJson(live[field], current[field]))
        && (!options.requirePublishingWithoutRuntimeContract
          || (live.buildStatus === 'publishing' && live.runtimeContract == null));
    let won: boolean;
    if (to === 'live') {
      if (!this.store.runTransaction) throw new PlatformError('unavailable', 'publication requires server transactions');
      won = await this.store.runTransaction(async tx => {
        const publication = options.compositionPublication;
        const jobLocks = publication ? await lockCompositionPublication(tx, publication, creationId, n, this.now(), current) : undefined;
        const compositionPolicies = jobLocks ? await lockCompositionMediaPolicies(this.store,tx,jobLocks) : undefined;
        const parent = compositionPolicies?.creations.get(creationId) ?? await tx.get<Creation>(creationDoc(creationId));
        if (!parent) throw new PlatformError('not-found', 'publication creation not found');
        const live = jobLocks ? (await lockCompositionVersions(tx,jobLocks)).target
          : await tx.get<CreationVersion>(versionDoc(creationId, n));
        if (!accepts(live)) return false;
        const gates = { ...current.gates, ...patch.gates };
        if (jobLocks) {
          const job=jobLocks.job;
          await assertCompositionPublicationProof(job, live!, gates, publication!.jobId, tx, compositionPolicies);
          const lineage = live!.executionPolicy?.compositionLineage;
          const conversion = live!.compositionConversion;
          const revision = live!.executionPolicy?.compositionRevision;
          // A revision replaces exactly the live game it was accepted against.
          const expectedActive = lineage && lineage.kind !== 'fork-copy'
            ? lineage.predecessor.version
            : conversion?.source.version ?? revision?.predecessor.version ?? 0;
          // Compare the accepted target head inside the publication transaction.
          if ((!lineage && !conversion && !revision && !isCompositionOriginHead(live!))
            || (lineage?.kind === 'fork-copy' && n !== 1)
            || (!!conversion && (conversion.source.creationId !== creationId
              || conversion.source.version >= n
              || conversion.target.authorityDigest !== live!.executionPolicy?.composition?.digest))
            || (!!revision && (revision.predecessor.creationId !== creationId
              || revision.predecessor.version >= n
              || revision.authorityDigest !== live!.executionPolicy?.composition?.digest))
            || parent.latestVersion !== n || (parent.activeVersion ?? 0) !== expectedActive
            || gates.demoRef?.version !== n || gates.demoRef.creationId !== creationId
            || gates.demoRef.artifactDigest !== gates.compositionProof?.distDigest
            || !/^[a-z0-9][a-z0-9._-]*$/.test(publication!.artifactBucket)
            || (patch.srcPath ?? current.srcPath) !== `gs://${publication!.artifactBucket}/${creationId}/${n}/src.${gates.srcSha256}.tgz`) {
            throw new PlatformError('conflict', 'Composed publication head or immutable artifact changed');
          }
        }
        const policy = new AssetRejectionsRepo(this.store);
        if (jobLocks) {
          // A composed game's soundtrack is its media child's: a generic-era
          // Studio sound draft (a creator-bound track) is retired at the
          // composed go-live, audited to the build's attribution, instead of
          // being held against the composed inventory as the generic sound
          // authority is below.
          await new SfxBindingsRepo(this.store).retire(tx, { creationId, creation: parent,
            actorUid: current.updatedBy, actorHandle: current.updatedByHandle, atMs: this.now() });
        } else {
          await policy.assertSfxCurrent(creationId, patch.gates?.mediaInventory ?? current.gates?.mediaInventory, tx);
        }
        await policy.assertInventoryAllowed(creationId, patch.assetsSnapshot ?? current.assetsSnapshot, parent.assetPolicyRevision ?? 0, tx);
        await policy.assertMaterializedAllowed(creationId, patch.gates?.mediaInventory ?? current.gates?.mediaInventory, parent.assetPolicyRevision ?? 0, tx);
        await tx.update(versionDoc(creationId, n), data);
        if (jobLocks && publication) {
          const job=jobLocks.job;
          await new WorkshopRepo(this.store).carryToLiveVersion(tx, { creationId, creation: parent, version: live!, atMs: this.now() });
          await tx.update(creationDoc(creationId), {
            buildState: 'idle', status: 'live', latestVersion: n, activeVersion: n,
            latestBundleUrl: gates.demoRef!.url, activeBundleUrl: gates.demoRef!.url,
            previewUrl: patch.previewUrl ?? current.previewUrl ?? null,
            posterUrl: patch.posterUrl ?? current.posterUrl ?? null,
            updatedAt: this.store.serverTimestamp(),
          });
          await tx.update(buildJobDoc(publication.jobId), {
            status: 'done', error: null, leaseExpiry: null, finishedAt: this.store.serverTimestamp(),
            measuredExecution: { ...job.measuredExecution!, ledgerRevision: (job.measuredExecution!.ledgerRevision ?? 0) + 1,
              traceSegments: job.measuredExecution!.traceSegments.map(row => row.segment === publication.segment
                ? { ...row, status: 'live', uri: publication.traceUri, finishedAt: this.now() } : row) },
          });
        }
        return true;
      });
    } else if (composed) {
      if (!this.store.runTransaction) throw new PlatformError('unavailable', 'composition lifecycle requires server transactions');
      won = await this.store.runTransaction(async tx => {
        const authority = options.compositionClaim!;
        const job = await tx.get<BuildJob>(buildJobDoc(authority.jobId));
        if (!job || job.status !== 'claimed' || job.creationId !== creationId || job.version !== n
          || !authority.claim.claimToken || job.workerId !== authority.claim.workerId
          || job.claimToken !== authority.claim.claimToken || job.leaseGeneration !== authority.claim.leaseGeneration
          || (job.leaseExpiry ?? 0) <= this.now()) return false;
        const live = await tx.get<CreationVersion>(versionDoc(creationId,n));
        if (live?.creationRun?.codeJobId !== authority.jobId || !accepts(live)) return false;
        await tx.update(versionDoc(creationId,n),data);
        return true;
      });
    } else {
      won = await this.store.guardedUpdate<CreationVersion>(versionDoc(creationId, n), accepts, data);
    }
    if (!won) {
      const reason = patch.runtimeContract
        ? 'runtime contract publication lost its immutable transition race'
        : options.requirePublishingWithoutRuntimeContract
          ? 'shadow runtime fallback lost its publishing transition race'
          : `build transition ${current.buildStatus} → ${to} lost its lifecycle race`;
      throw new PlatformError('conflict', `${reason} (version ${creationId}/${n})`);
    }
  }

  /** Replace only the on-demand rendered-audit report on a live row. Unlike a
   * same-state lifecycle transition this cannot alter terminal timestamps,
   * artifact paths, capability/runtime authority, or any sibling gate. */
  async stampLivePlaytestAudit(
    creationId: string,
    n: number,
    report: NonNullable<VersionGates['playtestAudit']>,
  ): Promise<boolean> {
    const current = await this.get(creationId, n);
    if (!current) {
      throw new PlatformError('not-found', `stampLivePlaytestAudit: version ${creationId}/${n} not found`);
    }
    if (current.buildStatus !== 'live') {
      throw new PlatformError(
        'conflict',
        `stampLivePlaytestAudit: version ${creationId}/${n} is ${current.buildStatus}, not live`,
      );
    }
    if (sameJson(current.gates.playtestAudit, report)) return false;
    const gates = { ...current.gates, playtestAudit: report };
    const won = await this.store.guardedUpdate<CreationVersion>(
      versionDoc(creationId, n),
      (live) => live !== null
        && live.buildStatus === 'live'
        && sameJson(live.gates, current.gates)
        && sameJson(live.runtimeContract, current.runtimeContract)
        && live.finishedAt === current.finishedAt,
      { gates },
    );
    if (!won) {
      throw new PlatformError(
        'conflict',
        `live audit changed while stamping ${creationId}/${n}`,
      );
    }
    return true;
  }

  /**
   * Stamp a LIVE version's splash-reel judgment (the reel backfill's writer;
   * publication stamps its own through {@link setStatus}). One transaction:
   * the version's `gates.splashReel` and clip URLs, mirrored onto the creation
   * only while this version is still its active one.
   *
   * - `recorded` with a `clip`: the new clip replaces any old one.
   * - `recorded` without a `clip`: the existing clip was judged moving; kept.
   * - `static`: the clip is a still, so the trailer shows the cover — cleared.
   * - `skipped`: nothing was learned about the clip — URLs untouched.
   */
  async stampLiveSplashReel(
    creationId: string,
    n: number,
    outcome: SplashReelOutcomeV1,
    clip?: { previewUrl: string; posterUrl: string },
  ): Promise<void> {
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'stampLiveSplashReel requires server transactions');
    if (clip && outcome.status !== 'recorded') throw new PlatformError('validation', 'only a recorded reel publishes a clip');
    await this.store.runTransaction(async (tx) => {
      const live = await tx.get<CreationVersion>(versionDoc(creationId, n));
      if (!live || live.buildStatus !== 'live') {
        throw new PlatformError('conflict', `stampLiveSplashReel: version ${creationId}/${n} is ${live?.buildStatus ?? 'missing'}, not live`);
      }
      const urls = clip
        ? { previewUrl: clip.previewUrl, posterUrl: clip.posterUrl }
        : outcome.status === 'static'
          ? { previewUrl: null, posterUrl: null }
          : { previewUrl: live.previewUrl ?? null, posterUrl: live.posterUrl ?? null };
      await tx.update(versionDoc(creationId, n), { gates: { ...live.gates, splashReel: outcome }, ...urls });
      const parent = await tx.get<Creation>(creationDoc(creationId));
      if (parent && (parent.activeVersion ?? 0) === n) await tx.update(creationDoc(creationId), urls);
    });
  }

  /**
   * Mirror the builder's live progress pointer onto the version doc — the ONE
   * writer of `buildProgress` (the studio Iterate tab's phase tracker). A plain
   * field update, deliberately outside {@link setStatus}: a progress write can
   * never move the lifecycle state machine, and the last pointer stays on the
   * doc through the terminal transition so a failed build shows where it
   * stopped.
   */
  async setProgress(creationId: string, n: number, progress: BuildProgress): Promise<void> {
    await this.store.update(versionDoc(creationId, n), { buildProgress: progress });
  }

  /** Record a real worker claim before the first build phase is emitted. The
   * queue repo owns the intervals; this copies only the member-readable timing
   * projection onto the version. Reclaims preserve the first worker start and
   * replace the accumulated queue breakdown with the job's newer record. */
  async recordWorkerStart(
    creationId: string,
    n: number,
    job: Pick<BuildJob, 'claimedAt' | 'queueIntervals' | 'queueMs'>,
  ): Promise<void> {
    const claimedAt = job.claimedAt ?? this.now();
    const queueIntervals = (job.queueIntervals ?? []).map((interval) => ({ ...interval }));
    const queueMs = job.queueMs ?? queueIntervals.reduce(
      (sum, interval) => sum + Math.max(0, (interval.claimedAt ?? claimedAt) - interval.enteredAt),
      0,
    );
    // Queue-entry and worker-start projections may cross after a fast reclaim.
    // Retry optimistic contention so the authoritative claimed job always
    // closes the open queue interval unless the lifecycle became terminal.
    for (;;) {
      const current = await this.get(creationId, n);
      if (!current) throw new PlatformError('not-found', `recordWorkerStart: version ${creationId}/${n} not found`);
      if (current.buildStatus === 'live' || current.buildStatus === 'failed') return;
      const nextTiming: NonNullable<CreationVersion['buildTiming']> = {
        acceptedAt: acceptedAtOrCreatedAt(current),
        workerStartedAt: current.buildTiming?.workerStartedAt ?? claimedAt,
        readyAt: null,
        queueIntervals,
        queueMs,
        buildDurationMs: null,
        totalElapsedMs: null,
      };
      if (JSON.stringify(current.buildTiming ?? null) === JSON.stringify(nextTiming)) return;
      const expectedStatus = current.buildStatus;
      const expectedTiming = JSON.stringify(current.buildTiming ?? null);
      const won = await this.store.guardedUpdate<CreationVersion>(versionDoc(creationId, n), (live) =>
        live !== null && live.buildStatus === expectedStatus &&
        JSON.stringify(live.buildTiming ?? null) === expectedTiming,
      { buildTiming: nextTiming });
      if (won) return;
    }
  }

  /** Project a requeue immediately so elapsed/progress views stop counting
   * builder waiting as worker time before the replacement worker claims it. */
  async recordQueueEntry(
    creationId: string,
    n: number,
    job: Pick<BuildJob, 'queueEnteredAt' | 'queueIntervals' | 'queueMs'>,
  ): Promise<void> {
    const queueEnteredAt = job.queueEnteredAt ?? this.now();
    const queueIntervals = [
      ...(job.queueIntervals ?? []).map((interval) => ({ ...interval })),
      { enteredAt: queueEnteredAt, claimedAt: null },
    ];
    for (;;) {
      const current = await this.get(creationId, n);
      if (!current) throw new PlatformError('not-found', `recordQueueEntry: version ${creationId}/${n} not found`);
      if (current.buildStatus === 'live' || current.buildStatus === 'failed') return;
      // A worker-start projection for this exact queue entry is newer evidence;
      // never reopen an interval that the claimed job has already closed.
      if (current.buildTiming?.queueIntervals.some((interval) =>
        interval.enteredAt === queueEnteredAt && interval.claimedAt !== null,
      )) return;
      const nextTiming: NonNullable<CreationVersion['buildTiming']> = {
        acceptedAt: acceptedAtOrCreatedAt(current),
        workerStartedAt: current.buildTiming?.workerStartedAt ?? null,
        readyAt: null,
        queueIntervals,
        queueMs: job.queueMs ?? current.buildTiming?.queueMs ?? 0,
        buildDurationMs: null,
        totalElapsedMs: null,
      };
      if (JSON.stringify(current.buildTiming ?? null) === JSON.stringify(nextTiming)) return;
      const expectedStatus = current.buildStatus;
      const expectedTiming = JSON.stringify(current.buildTiming ?? null);
      const won = await this.store.guardedUpdate<CreationVersion>(versionDoc(creationId, n), (live) =>
        live !== null && live.buildStatus === expectedStatus &&
        JSON.stringify(live.buildTiming ?? null) === expectedTiming,
      { buildTiming: nextTiming });
      if (won) return;
    }
  }

  /**
   * Stamp a {@link ManifestSummary} onto an already-LIVE version that predates
   * the field — the manifest-summary BACKFILL's writer (new versions get theirs
   * from the builder via {@link setStatus} at publish; this path never touches
   * buildStatus or finishedAt). Rejects non-live versions (`conflict`); a
   * version already carrying a summary is left untouched (returns false —
   * idempotent re-runs).
   */
  async stampManifestSummary(
    creationId: string,
    n: number,
    summary: ManifestSummary,
  ): Promise<boolean> {
    const current = await this.get(creationId, n);
    if (!current) {
      throw new PlatformError('not-found', `stampManifestSummary: version ${creationId}/${n} not found`);
    }
    if (current.buildStatus !== 'live') {
      throw new PlatformError(
        'conflict',
        `stampManifestSummary: version ${creationId}/${n} is ${current.buildStatus}, not live`,
      );
    }
    if (current.manifestSummary) return false;
    await this.store.update(versionDoc(creationId, n), { manifestSummary: summary });
    return true;
  }

  /**
   * Correct the `auditVersion` on an already-LIVE version's Tier-1 report — the
   * audit-version REPAIR's writer, for rows whose stamp names the wrong
   * instrument because `AUDIT_SCHEMA_VERSION` (types.ts) was not bumped when
   * the audit changed what it measures.
   *
   * Deliberately NOT `setStatus`: that refreshes `finishedAt` on every live
   * transition, and `finishedAt` is the only record of WHEN a report was
   * stamped — the one fact that says which instrument produced it. Repairing
   * through `setStatus` would erase the evidence the repair is derived from.
   *
   * Rejects a version that is not live or carries no stamped Tier-1
   * `auditVersion` (`conflict`) — the pre-versioning era is its own bucket and
   * is never back-stamped. Returns false when the value is already correct, so
   * re-runs are idempotent.
   */
  async restampAuditVersion(creationId: string, n: number, auditVersion: number): Promise<boolean> {
    const current = await this.get(creationId, n);
    if (!current) {
      throw new PlatformError('not-found', `restampAuditVersion: version ${creationId}/${n} not found`);
    }
    if (current.buildStatus !== 'live') {
      throw new PlatformError(
        'conflict',
        `restampAuditVersion: version ${creationId}/${n} is ${current.buildStatus}, not live`,
      );
    }
    const audit = current.gates?.playtestAudit;
    const tier1 = audit?.tier1;
    if (!tier1 || tier1.auditVersion === undefined) {
      throw new PlatformError(
        'conflict',
        `restampAuditVersion: version ${creationId}/${n} carries no stamped tier-1 auditVersion`,
      );
    }
    if (tier1.auditVersion === auditVersion) return false;
    await this.store.update(versionDoc(creationId, n), {
      gates: {
        ...current.gates,
        playtestAudit: { ...audit, tier1: { ...tier1, auditVersion } },
      },
    });
    return true;
  }

  /**
   * Delete one version doc. The OWNER-ONLY operation of the collaboration
   * model (editors may build/roll back but never destroy history — the
   * deleteVersion function gates on owner-or-admin; this repo enforces the
   * data invariants regardless of caller):
   *   - the ACTIVE (played) version can never be deleted — roll the active
   *     pointer elsewhere first (`conflict`);
   *   - a missing version throws `not-found` (an explicit action on a stale
   *     row should surface, not silently no-op).
   * `latestVersion` stays a HIGH-WATER mark so `append` never reuses a number.
   * GCS artifacts (bundle/src tarball) are left in place — consistent with the
   * purge tooling; the docs note the orphaning.
   */
  async delete(creationId: string, n: number): Promise<void> {
    const version = await this.get(creationId, n);
    if (!version) {
      throw new PlatformError('not-found', `delete: version ${creationId}/${n} not found`);
    }
    const parent = await this.store.getDoc<{ activeVersion?: number }>(creationDoc(creationId));
    if (!parent) {
      throw new PlatformError('not-found', `delete: creation ${creationId} not found`);
    }
    if ((parent.activeVersion ?? 0) === n) {
      throw new PlatformError(
        'conflict',
        `delete: version ${n} is the ACTIVE version of ${creationId} — set another version active first`,
      );
    }
    await this.store.batch([{ type: 'delete', path: versionDoc(creationId, n) }]);
  }
}
